import { Flatfile } from '@flatfile/api-internal'
import { Button, Text, Popover, PopoverContent, PopoverTrigger } from '@flatfile/baseline'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { ChartType, MetricChart } from '../../components/Analytics/MetricChart'
import { DashboardDatePickerWidget } from '../../components/Analytics/DatePicker'
import { MetricList } from '../../components/Analytics/MetricList'
import { useCollection } from '../../resources/hooks/useCollection'
import { ListMetric } from './utilities/customAppHelpers'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { getFormattedNumber, getFormattedNumberToMillions } from '../../utils/format'

enum StatCategory {
  FILES = 'Files Uploaded',
  PDV = 'PDV',
  USERS = 'File Uploaders',
  MATCH_ACCURACY = 'Match accuracy',
}

const CategoryMapping = {
  [StatCategory.FILES]: {
    name: [StatCategory.FILES].toString(),
    tooltip: "Counts all uploaded files, including those that didn't complete field mapping.",
    title: 'Files uploaded by newest',
    chartType: ChartType.LINE,
    chartMetrics: 'fileCountByDay',
    listMetrics: 'fileList',
    desc: undefined,
  },
  [StatCategory.PDV]: {
    name: [StatCategory.PDV].toString(),
    tooltip:
      'Counts Processed Data Values (PDVs) in all of your Flatfile Workbooks. PDVs are the total data points, akin to spreadsheet cells, determined by the product of rows and columns. Note: Development environments are exempt from charges.',
    title: 'PDV by newest',
    chartType: ChartType.LINE,
    chartMetrics: 'pdvCountByDay',
    listMetrics: 'pdvBySpaceList',
    desc: undefined,
  },
  [StatCategory.USERS]: {
    name: [StatCategory.USERS].toString(),
    tooltip:
      'Tracks the total number of users who uploaded files, covering both completed and incomplete field mapping sessions. For anonymous guests, a user is counted once per session; however, if they exit and start a new session, they are counted again.',
    title: 'File uploaders by newest',
    chartType: ChartType.LINE,
    chartMetrics: 'userCountByDay',
    listMetrics: 'usersList',
    desc: undefined,
  },
  [StatCategory.MATCH_ACCURACY]: {
    name: [StatCategory.MATCH_ACCURACY].toString(),
    tooltip:
      'Match accuracy indicates the percentage of fields accurately auto-mapped by our system. Mappings accepted by end user increase accuracy, while incorrect mappings, including those that should have been ignored, decrease it.',
    title: 'Match accuracy',
    chartType: ChartType.LINE,
    chartMetrics: 'matchAccuracyByDay',
    listMetrics: undefined,
    desc: '%',
  },
}

export const CustomDashboardPage = () => {
  const { environment } = useActiveEnvironment()
  let { id = '' } = useParams()
  let { namespace } = useParams()
  const apps = useCollection('APPS')
  const currentApp = useMemo(
    () => apps.find((app: Flatfile.App) => app.id === id || app.namespace === namespace),
    [apps, id, namespace]
  )
  if (currentApp) {
    namespace = currentApp.namespace
    id = currentApp.id
  }
  const now = new Date()
  const daysAgo30 = new Date(new Date().setDate(now.getDate() - 30))
  const [pageDateValue, setStartDateValue] = useState<[Date, Date]>([daysAgo30, now])

  const [startDate, endDate] = pageDateValue.map((date) => format(date, 'yyyy-MM-dd'))
  const [readableStartDate, readableEndDate] = pageDateValue.map((date) => format(date, 'LLL dd, yyyy'))

  const [activeCategory, setActiveCategory] = useState<StatCategory>(StatCategory.FILES)

  const { addToRefsArray, listHeight } = useDynamicHeight()

  return (
    <div className='w-full'>
      <div ref={addToRefsArray} className='h-[63px]'>
        <div className='flex justify-between px-6 py-3'>
          <Text className='text-lg font-semibold'>Dashboard</Text>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant='outline' size='sm' icon='calendar-3' iconProps={{ size: 16, className: 'mr-2' }}>
                {readableStartDate} - {readableEndDate}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto'>
              <DashboardDatePickerWidget daysAgo30={daysAgo30} now={now} setStartDateValue={setStartDateValue} />
            </PopoverContent>
          </Popover>
        </div>
        <div className='h-px bg-border' />
      </div>
      <div ref={addToRefsArray} className='min-h-[355px] bg-[#FBFCFF] flex flex-col'>
        <div className='grid grid-cols-12 gap-4 px-6 py-6 flex-grow'>
          <div className='col-span-12 lg:col-span-6 xl:col-span-5'>
            <div className='grid grid-cols-2 gap-4 h-full'>
              <div className='col-span-1 h-full flex flex-col'>
                <MetricChart
                  formatter={getFormattedNumber}
                  chartType={ChartType.LINE}
                  title={StatCategory.FILES}
                  tooltip={CategoryMapping[StatCategory.FILES].tooltip}
                  currentApp={currentApp}
                  environment={environment}
                  startDate={startDate}
                  endDate={endDate}
                  metrics={CategoryMapping[StatCategory.FILES].chartMetrics}
                  simplified
                  expanded={activeCategory === StatCategory.FILES}
                  onClickViewMore={() => setActiveCategory(StatCategory.FILES)}
                />
              </div>
              <div className='col-span-1 h-full flex flex-col'>
                <MetricChart
                  formatter={getFormattedNumberToMillions}
                  chartType={ChartType.LINE}
                  title={StatCategory.PDV}
                  tooltip={CategoryMapping[StatCategory.PDV].tooltip}
                  currentApp={currentApp}
                  environment={environment}
                  startDate={startDate}
                  endDate={endDate}
                  metrics={CategoryMapping[StatCategory.PDV].chartMetrics}
                  simplified
                  expanded={activeCategory === StatCategory.PDV}
                  onClickViewMore={() => setActiveCategory(StatCategory.PDV)}
                />
              </div>
              <div className='col-span-1 h-full flex flex-col'>
                <MetricChart
                  formatter={getFormattedNumber}
                  chartType={ChartType.LINE}
                  title={StatCategory.USERS}
                  tooltip={CategoryMapping[StatCategory.USERS].tooltip}
                  currentApp={currentApp}
                  environment={environment}
                  startDate={startDate}
                  endDate={endDate}
                  metrics={CategoryMapping[StatCategory.USERS].chartMetrics}
                  simplified
                  expanded={activeCategory === StatCategory.USERS}
                  onClickViewMore={() => setActiveCategory(StatCategory.USERS)}
                />
              </div>
              <div className='col-span-1 h-full flex flex-col'>
                <MetricChart
                  chartType={ChartType.LINE}
                  title={StatCategory.MATCH_ACCURACY}
                  tooltip={CategoryMapping[StatCategory.MATCH_ACCURACY].tooltip}
                  desc={CategoryMapping[StatCategory.MATCH_ACCURACY].desc}
                  currentApp={currentApp}
                  environment={environment}
                  startDate={startDate}
                  endDate={endDate}
                  metrics={CategoryMapping[StatCategory.MATCH_ACCURACY].chartMetrics}
                  simplified
                  expanded={activeCategory === StatCategory.MATCH_ACCURACY}
                  onClickViewMore={() => setActiveCategory(StatCategory.MATCH_ACCURACY)}
                />
              </div>
            </div>
          </div>
          <div className='col-span-12 lg:col-span-6 xl:col-span-7 h-full flex flex-col'>
            <MetricChart
              name={CategoryMapping[activeCategory].name}
              tooltip={CategoryMapping[activeCategory].tooltip}
              chartType={CategoryMapping[activeCategory].chartType}
              title={CategoryMapping[activeCategory].title}
              currentApp={currentApp}
              environment={environment}
              startDate={startDate}
              endDate={endDate}
              metrics={CategoryMapping[activeCategory].chartMetrics}
              desc={CategoryMapping[activeCategory].desc}
            />
          </div>
        </div>
        <div className='h-px bg-border' />
      </div>
      {CategoryMapping[activeCategory].listMetrics && (
        <MetricList
          title={CategoryMapping[activeCategory].title}
          tooltip={CategoryMapping[activeCategory].tooltip}
          height={listHeight}
          currentApp={currentApp}
          environment={environment}
          startDate={startDate}
          endDate={endDate}
          metrics={CategoryMapping[activeCategory].listMetrics! as ListMetric}
        />
      )}
    </div>
  )
}

export default CustomDashboardPage
